import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Container, Typography, Box, Card, CardContent, 
  List, ListItem, ListItemText, ListItemAvatar, Avatar, 
  Divider, CircularProgress, Chip
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { apiService } from '../utils/api';
import { normalizeImageUrl } from '../components/Post';
import './ListDetail.css';

const ListDetail = () => {
  // ルート定義は /lists/:listId のためパラメータ名は listId。id にエイリアスする。
  const { listId, id: idParam } = useParams();
  const id = listId || idParam;
  const [listDetails, setListDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // リスト詳細を取得
  useEffect(() => {
    const fetchListDetails = async () => {
      try {
        setLoading(true);
        
        const response = await apiService.get(`/api/lists/${id}`, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          params: {
            _t: Date.now() // キャッシュ回避のためのタイムスタンプ
          }
        });
        
        
        setListDetails(response.data);
        setLoading(false);
      } catch (error) {
        
        setError('リスト情報の取得に失敗しました。');
        setLoading(false);
      }
    };

    if (id) {
      fetchListDetails();
    }
  }, [id]);

  // リストアイコンを表示（公開/非公開）
  const getListIcon = (isPrivate) => {
    return isPrivate ? 
      <LockIcon fontSize="small" style={{ marginRight: 8 }} /> : 
      <PublicIcon fontSize="small" style={{ marginRight: 8, color: '#1976d2' }} />;
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" my={5}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Box mt={3}>
          <Typography variant="h6" color="error">{error}</Typography>
          <Box mt={2}>
            <Link to="/lists" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>
              <ArrowBackIcon fontSize="small" style={{ marginRight: 8 }} />
              リスト一覧に戻る
            </Link>
          </Box>
        </Box>
      </Container>
    );
  }

  if (!listDetails) {
    return (
      <Container>
        <Box mt={3}>
          <Typography variant="h6">リストが見つかりませんでした</Typography>
          <Box mt={2}>
            <Link to="/lists" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>
              <ArrowBackIcon fontSize="small" style={{ marginRight: 8 }} />
              リスト一覧に戻る
            </Link>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Box mt={3} mb={1}>
        <Link to="/lists" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>
          <ArrowBackIcon fontSize="small" style={{ marginRight: 8 }} />
          リスト一覧に戻る
        </Link>
      </Box>

      <Card className="list-detail__card">
        <CardContent>
          <Box display="flex" alignItems="center" mb={1}>
            {getListIcon(listDetails.is_private)}
            <Typography variant="h4" component="h1">
              {listDetails.name}
            </Typography>
          </Box>
          
          <Box mb={2}>
            <Chip 
              icon={getListIcon(listDetails.is_private)} 
              label={listDetails.is_private ? "非公開リスト" : "公開リスト"} 
              variant="outlined" 
              color={listDetails.is_private ? "default" : "primary"}
            />
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              {listDetails.members_count}人のメンバー
            </Typography>
          </Box>

          {listDetails.description && (
            <Box mb={3}>
              <Typography variant="body1">
                {listDetails.description}
              </Typography>
            </Box>
          )}
          
          <Divider style={{ margin: '16px 0' }} />
          
          <Typography variant="h6" gutterBottom>メンバー</Typography>
          
          {listDetails.members && listDetails.members.length > 0 ? (
            <List>
              {listDetails.members.map((member) => (
                <React.Fragment key={member.id}>
                  <ListItem 
                    component={Link} 
                    to={`/profile/${member.username}`}
                    className="list-detail__memberItem"
                  >
                    <ListItemAvatar>
                      <Avatar 
                        src={normalizeImageUrl(member.avatar) || '/images/default-avatar.png'} 
                        alt={member.display_name || member.username} 
                      />
                    </ListItemAvatar>
                    <ListItemText 
                      primary={member.display_name || member.username} 
                      secondary={`@${member.username}`} 
                    />
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="textSecondary">
              このリストにはまだメンバーがいません。
            </Typography>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default ListDetail; 